import { MapPin, Plus, SlidersHorizontal, X } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { useCreateProductRequest } from "@/api/product-request";
import { useStates } from "@/api/util";
import { ItemSearch } from "@/components/dashboard/search";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage
} from "@/components/ui/form";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import useAuthStore from "@/store/auth";
import { zodResolver } from "@hookform/resolvers/zod";

import { NumInputSeparated } from "../ui/num-input-separated";

interface ProductRequestFormProps {
	onRequestCreated?: (requestId: string) => void;
}

interface Filter {
	id: "quantity" | "state";
	label: string;
	placeholder: string;
}

const AVAILABLE_FILTERS: Filter[] = [
	{
		id: "quantity",
		label: "Product Quantity",
		placeholder: "Enter quantity"
	},
	{
		id: "state",
		label: "Vendor's Preferred State",
		placeholder: "Enter state"
	}
];

const productRequestSchema = z.object({
	productDescription: z.string().min(1, "Description is required"),
	quantity: z.number().int().positive("Quantity must be a positive number").optional(),
	preferredState: z.string().optional()
});

type ProductRequestFormValues = z.infer<typeof productRequestSchema>;

export function ProductRequestForm({ onRequestCreated }: ProductRequestFormProps) {
	const [activeFilters, setActiveFilters] = useState<Filter["id"][]>([]);
	const createRequest = useCreateProductRequest();

	const form = useForm<ProductRequestFormValues>({
		resolver: zodResolver(productRequestSchema),
		defaultValues: {
			productDescription: "",
			quantity: undefined,
			preferredState: undefined
		}
	});

	const user = useAuthStore(s => s.user);
	const statesQuery = useStates(user?.countryId);

	const handleSubmit = async (values: ProductRequestFormValues) => {
		try {
			if (!values.preferredState)
				delete values.preferredState;

			if (!values.quantity)
				delete values.quantity;

			const result = await createRequest.mutateAsync(values);
			onRequestCreated?.(result.id);
		}
		catch (error) {
			console.error("Failed to create request:", error);
		}
	};

	const addFilter = (filterId: Filter["id"]) => {
		setActiveFilters(prev => [...prev, filterId]);
	};

	const removeFilter = (filterId: Filter["id"]) => {
		setActiveFilters(prev => prev.filter(id => id !== filterId));
		// Reset the value when removing the filter
		if (filterId === "quantity")
			form.setValue("quantity", 1);
		if (filterId === "state")
			form.setValue("preferredState", "");
	};

	const availableFilters = AVAILABLE_FILTERS.filter(
		filter => !activeFilters.includes(filter.id)
	);

	return (
		<Card className="shadow-none border-none">
			<div className="p-6 space-y-6">
				<div>
					<h1 className="text-xl mb-2 font-semibold">Request Product</h1>
					<p className="text-muted-foreground text-xs">
						Looking for a specific product? Fill out the request form below and our system will connect you with a select vendor.
					</p>
				</div>

				<Form {...form}>
					<form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
						<FormField
							control={form.control}
							name="productDescription"
							render={({ field }) => (
								<FormItem>
									<FormLabel>What are you looking for?</FormLabel>
									<FormControl>
										<Textarea
											placeholder="Please provide details like brand, size, color, etc."
											className="min-h-[100px] resize-none"
											{...field}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<div className="space-y-4">
							{availableFilters.length > 0 && (
								<DropdownMenu>
									<DropdownMenuTrigger asChild>
										<Button
											type="button"
											variant="outline"
											className="gap-2"
										>
											<Plus className="size-4" />
											Add Filter
											<SlidersHorizontal className="size-4 ml-2" />
										</Button>
									</DropdownMenuTrigger>
									<DropdownMenuContent align="start">
										{availableFilters.map(filter => (
											<DropdownMenuItem
												key={filter.id}
												onClick={() => addFilter(filter.id)}
											>
												{filter.label}
											</DropdownMenuItem>
										))}
									</DropdownMenuContent>
								</DropdownMenu>
							)}

							<div className="space-y-4">
								{activeFilters.map((filterId) => {
									const filter = AVAILABLE_FILTERS.find(f => f.id === filterId)!;
									return (
										<div key={filter.id} className="space-y-2">
											<div className="flex items-center justify-between">
												<Label>{filter.label}</Label>
												<Button
													type="button"
													variant="destructive"
													size="sm"
													className="h-auto p-1"
													onClick={() => removeFilter(filter.id)}
												>
													<X className="size-4" />
												</Button>
											</div>
											{filter.id === "quantity"
												? (
														<FormField
															control={form.control}
															name="quantity"
															render={({ field }) => (
																<FormItem>
																	<FormControl>
																		<NumInputSeparated
																			placeholder={filter.placeholder}
																			value={field.value?.toString() || ""}
																			onChange={value => field.onChange(+value)}
																		/>
																	</FormControl>
																	<FormMessage />
																</FormItem>
															)}
														/>
													)
												: (
														<FormField
															control={form.control}
															name="preferredState"
															render={() => (
																<FormItem>
																	<FormControl>
																		<ItemSearch
																			control={form.control}
																			name="preferredState"
																			items={statesQuery.data ?? []}
																			isLoading={statesQuery.isPending}
																			label=""
																			placeholder="Select state"
																			searchPlaceholder="Search state"
																			emptyMessage="No state found"
																			icon={<MapPin size={20} />}
																		/>
																	</FormControl>
																	<FormMessage />
																</FormItem>
															)}
														/>
													)}
										</div>
									);
								})}
							</div>
						</div>

						<Button
							type="submit"
							className="w-full"
							disabled={createRequest.isPending}
						>
							{createRequest.isPending ? "Submitting..." : "Request"}
						</Button>
					</form>
				</Form>
			</div>
		</Card>
	);
}
